import { BUSINESS_INFO } from '../data/business.data';
import { FAQ_ITEMS } from '../data/faq.data';
import { OFFERS, getOffer, minPrice, priceShort, priceUnitText } from '../data/offers.data';
import { SITE } from '../data/site.data';
import { canonicalUrl } from '../data/url.utility';

export interface SeoData {
  /** Full <title> content (without site suffix appended automatically). */
  readonly title: string;
  /** Meta description; also used for og:description and twitter:description. */
  readonly description: string;
  /** Optional override image (absolute URL). Falls back to SITE.defaultImage. */
  readonly image?: string;
  /** Optional robots meta override; defaults to 'index, follow'. */
  readonly robots?: string;
  /** Optional JSON-LD object(s) attached as <script type="application/ld+json"> for this route. */
  readonly jsonLd?: () => object | readonly object[];
}

const offerSummary = OFFERS.map(
  o => `${o.title === 'Unbetreut' && o.id === 'kids' ? 'Kids' : o.title} ${priceShort(minPrice(o))}`,
).join(', ');

const SEO_HOME: SeoData = {
  title: `Bubble Soccer mieten in der Ostschweiz | ${SITE.brand}`,
  description: `Bubble Soccer mieten in der Ostschweiz. Betreut (ab ${priceShort(minPrice(getOffer('betreut')))}) und unbetreut (ab ${priceShort(minPrice(getOffer('unbetreut')))}) für Polteranlass, Teambuilding, Geburtstag, Firmenfeier und mehr. Jetzt online buchen!`,
  jsonLd: () => [websiteSchema(), localBusinessSchema()],
};

const SEO_OFFER: SeoData = {
  title: `Angebot - Bubble Soccer Pakete & Preise | ${SITE.brand}`,
  description: `Bubble Soccer Angebote: ${offerSummary}. Inklusive 10 Bubbles, mit optionalem Platz und Zusatz-Bubbles. Vergleichen Sie unsere Pakete und buchen Sie online.`,
  jsonLd: () => OFFERS.map(o => productSchema(o.id)),
};

const SEO_BOOKING: SeoData = {
  title: `Online buchen - Bubble Soccer Buchung | ${SITE.brand}`,
  description: `Bubble Soccer online buchen: Angebot wählen, Datum und Uhrzeit festlegen, Anfrage senden. Mit Preis-Kalkulator für Betreut (${priceShort(minPrice(getOffer('betreut')))}), Unbetreut (${priceShort(minPrice(getOffer('unbetreut')))}) und Kids (${priceShort(minPrice(getOffer('kids')))}). Ostschweiz.`,
};

const SEO_ABOUT: SeoData = {
  title: `Über uns - Das Team hinter ${SITE.brand} Bubble Soccer`,
  description: `Lernen Sie das vierköpfige Team von ${BUSINESS_INFO.legalName} kennen, das Bubble Soccer in der Ostschweiz vermietet. Unsere Mission: Spass, Gemeinschaft und sichere Events.`,
};

const SEO_CONTACT: SeoData = {
  title: `Kontakt & FAQ - Bubble Soccer anfragen | ${SITE.brand}`,
  description: `Kontaktieren Sie ${BUSINESS_INFO.legalName} per Telefon (${BUSINESS_INFO.phoneDisplay}), E-Mail (${BUSINESS_INFO.email}) oder Kontaktformular. Antworten zu Buchung, Sicherheit und Ablauf finden Sie in unserem FAQ.`,
  jsonLd: () => faqPageSchema(),
};

const SEO_IMPRINT: SeoData = {
  title: `Impressum | ${SITE.brand} Bubble Soccer`,
  description: `Impressum der ${BUSINESS_INFO.legalName}, ${BUSINESS_INFO.street}, ${BUSINESS_INFO.postalCode} ${BUSINESS_INFO.city}. UID ${BUSINESS_INFO.uid}.`,
};

const SEO_AGB: SeoData = {
  title: `AGB - Allgemeine Geschäftsbedingungen | ${SITE.brand}`,
  description: `Allgemeine Geschäftsbedingungen der ${BUSINESS_INFO.legalName}: Vertragsabschluss, Preise in CHF, Zahlung, ${BUSINESS_INFO.cancellationHours}h-Stornofrist, Wetterklausel, Schadenregelung und schweizerisches Recht.`,
};

export const SEO = {
  home: SEO_HOME,
  offer: SEO_OFFER,
  booking: SEO_BOOKING,
  about: SEO_ABOUT,
  contact: SEO_CONTACT,
  imprint: SEO_IMPRINT,
  agb: SEO_AGB,
} as const;

// --- JSON-LD builders ---

function websiteSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    'name': SITE.name,
    'url': canonicalUrl('/'),
    'inLanguage': SITE.locale.replace('_', '-'),
  };
}

function localBusinessSchema(): object {
  const allPrices = OFFERS.flatMap(o => o.prices.map(p => p.amount));
  const min = Math.min(...allPrices);
  const max = Math.max(...allPrices);
  return {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    'name': BUSINESS_INFO.displayName,
    'legalName': BUSINESS_INFO.legalName,
    'description':
      'Bubble Soccer mieten in der Ostschweiz. Betreut und unbetreut für Events, Teambuilding, Polteranlass, Kindergeburtstag und mehr.',
    'url': canonicalUrl('/'),
    'telephone': BUSINESS_INFO.phoneE164,
    'email': BUSINESS_INFO.email,
    'address': {
      '@type': 'PostalAddress',
      'streetAddress': BUSINESS_INFO.street,
      'addressLocality': BUSINESS_INFO.city,
      'postalCode': BUSINESS_INFO.postalCode,
      'addressRegion': SITE.geoRegion.split('-')[1],
      'addressCountry': BUSINESS_INFO.countryCode,
    },
    'geo': {
      '@type': 'GeoCoordinates',
      'latitude': SITE.geoLatitude,
      'longitude': SITE.geoLongitude,
    },
    'areaServed': {
      '@type': 'GeoCircle',
      'geoMidpoint': {
        '@type': 'GeoCoordinates',
        'latitude': SITE.geoLatitude,
        'longitude': SITE.geoLongitude,
      },
      'geoRadius': String(SITE.serviceRadiusMeters),
    },
    'priceRange': `${BUSINESS_INFO.currency} ${min} - ${BUSINESS_INFO.currency} ${max}`,
    'currenciesAccepted': BUSINESS_INFO.currency,
    'paymentAccepted': BUSINESS_INFO.paymentMethods.join(', '),
    'sameAs': [BUSINESS_INFO.social.instagram, BUSINESS_INFO.social.linkedin],
    'hasOfferCatalog': {
      '@type': 'OfferCatalog',
      'name': 'Bubble Soccer Angebote',
      'itemListElement': OFFERS.map(o => {
        const p = minPrice(o);
        return {
          '@type': 'Offer',
          'name': `Bubble Soccer ${o.title}${o.id === 'kids' ? ' Kids' : ''}`,
          'description': o.summary,
          'priceCurrency': BUSINESS_INFO.currency,
          'price': String(p.amount),
          'priceSpecification': {
            '@type': 'UnitPriceSpecification',
            'price': String(p.amount),
            'priceCurrency': BUSINESS_INFO.currency,
            'unitText': priceUnitText(p.unit),
          },
        };
      }),
    },
  };
}

function productSchema(offerId: ReturnType<typeof getOffer>['id']): object {
  const o = getOffer(offerId);
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    'serviceType': `Bubble Soccer ${o.title}${o.id === 'kids' ? ' Kids' : ''}`,
    'description': o.summary,
    'provider': {
      '@type': 'LocalBusiness',
      'name': BUSINESS_INFO.displayName,
      'url': canonicalUrl('/'),
    },
    'areaServed': 'Ostschweiz',
    'offers': o.prices.map(p => ({
      '@type': 'Offer',
      'price': String(p.amount),
      'priceCurrency': BUSINESS_INFO.currency,
      'priceSpecification': {
        '@type': 'UnitPriceSpecification',
        'price': String(p.amount),
        'priceCurrency': BUSINESS_INFO.currency,
        'unitText': priceUnitText(p.unit),
        ...(p.duration === undefined
          ? {}
          : {
              referenceQuantity: {
                '@type': 'QuantitativeValue',
                'value': p.duration,
                'unitText': priceUnitText(p.unit),
              },
            }),
      },
      'availability': 'https://schema.org/InStock',
      'url': `${canonicalUrl('angebot')}#${o.anchor}`,
    })),
  };
}

function faqPageSchema(): object {
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    'mainEntity': FAQ_ITEMS.map(item => ({
      '@type': 'Question',
      'name': item.question,
      'acceptedAnswer': {
        '@type': 'Answer',
        'text': item.answer,
      },
    })),
  };
}

// re-export helper for templates that need a formatted price

export { formatChf } from '../data/offers.data';
